import AssetImage from './AssetImage';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  Eye,
  Film,
  Flag,
  Lock,
  RotateCcw,
  Star,
  Trash2,
} from 'lucide-react';

export interface AssetCardProps {
  asset: Asset;
  onView: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onRestore: (asset: Asset) => void;
  /** True while this asset's delete/restore request is in flight. */
  pending?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

const isVideo = (a: Asset) => (a.kind ? a.kind === 'video' : a.mime_type?.startsWith('video/') ?? false);

/** Real moderation state, derived from the row's flags (no fabricated values). */
export const assetState = (a: Asset) => {
  if (a.moderation?.status === 'rejected') return { label: 'Rejected', cls: 'bg-red-100 text-red-700' };
  if (a.moderation?.status === 'hidden') return { label: 'Hidden', cls: 'bg-orange-100 text-orange-700' };
  if (a.deleted === 1) return { label: 'Deleted', cls: 'bg-red-100 text-red-700' };
  if (a.corrupt === 1) return { label: 'Corrupt', cls: 'bg-amber-100 text-amber-700' };
  if (a.active === 1) return { label: 'Active', cls: 'bg-green-100 text-green-700' };
  return { label: 'Inactive', cls: 'bg-gray-100 text-gray-600' };
};

const AssetCard = ({ asset, onView, onDelete, onRestore, pending, selected, onToggleSelect }: AssetCardProps) => {
  const deleted = asset.deleted === 1;
  const state = assetState(asset);
  const video = isVideo(asset);

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md',
        deleted && 'opacity-70',
        selected && 'ring-2 ring-blue-500',
      )}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        <AssetImage
          asset={asset}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />

        {/* Top-left: state + attribute badges */}
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', state.cls)}>
            {state.label}
          </span>
          {asset.tester === 1 && (
            <span className="rounded-full bg-gray-800/70 px-1.5 py-0.5 text-[10px] font-medium text-white">Test</span>
          )}
          {video && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              <Film className="h-3 w-3" /> Video
            </span>
          )}
        </div>

        {/* Top-right: quick attribute icons */}
        <div className="absolute right-2 top-2 flex gap-1">
          {asset.featured === 1 && (
            <span className="rounded-full bg-yellow-400/90 p-1 text-white" title="Featured">
              <Star className="h-3 w-3" />
            </span>
          )}
          {asset.private === 1 && (
            <span className="rounded-full bg-slate-700/80 p-1 text-white" title="Private">
              <Lock className="h-3 w-3" />
            </span>
          )}
          {asset.moderation?.flagged === 1 && (
            <span className="rounded-full bg-blue-600/90 p-1 text-white" title={`Flagged: ${asset.moderation.flag_note ?? ''}`}>
              <Flag className="h-3 w-3" />
            </span>
          )}
          {asset.corrupt === 1 && (
            <span className="rounded-full bg-amber-500/90 p-1 text-white" title="Flagged corrupt">
              <AlertTriangle className="h-3 w-3" />
            </span>
          )}
        </div>

        {onToggleSelect && (
          <input
            type="checkbox"
            aria-label={`Select asset ${asset.asid}`}
            checked={!!selected}
            onChange={onToggleSelect}
            className={cn(
              'absolute bottom-2 left-2 z-10 h-4 w-4 cursor-pointer',
              !selected && 'opacity-0 group-hover:opacity-100 focus:opacity-100',
            )}
          />
        )}

        {/* Hover actions */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
          <Button variant="secondary" size="sm" onClick={() => onView(asset)}>
            <Eye className="mr-1 h-3.5 w-3.5" /> View
          </Button>
          {deleted ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={() => onRestore(asset)}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
            </Button>
          ) : (
            <Button
              variant="destructive"
              size="sm"
              disabled={pending}
              onClick={() => onDelete(asset)}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="p-3">
        <h3 className="truncate text-sm font-medium" title={asset.name}>
          {asset.name || `Asset #${asset.asid}`}
        </h3>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">{asset.mime_type || 'unknown'}</span>
          <span>•</span>
          <span className="shrink-0">
            {asset.date_added ? new Date(asset.date_added).toLocaleDateString() : '—'}
          </span>
        </div>
        {asset.eid ? (
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Listing #{asset.eid}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default AssetCard;
